<script>
import * as Sentry from '@sentry/browser';
import { mapGetters } from 'vuex';
import router from '../dashboard/routes';
import AddAccountModal from '../dashboard/components/layout/sidebarComponents/AddAccountModal.vue';
import LoadingState from './components/widgets/LoadingState.vue';
import NetworkNotification from './components/NetworkNotification.vue';
import UpgradeBanner from './components/app/UpgradeBanner.vue';
import PaymentPendingBanner from './components/app/PaymentPendingBanner.vue';
import PendingEmailVerificationBanner from './components/app/PendingEmailVerificationBanner.vue';
import vueActionCable from './helper/actionCable';
import WootSnackbarBox from './components/SnackbarContainer.vue';
import { setColorTheme } from './helper/themeHelper';
import { isOnOnboardingView } from 'v3/helpers/RouteHelper';
import {
  registerSubscription,
  verifyServiceWorkerExistence,
} from './helper/pushHelper';
import ReconnectService from 'dashboard/helper/ReconnectService';
import { setDatadogRumUser } from '../helper/datadogRum';
import {
  setAmplitudeUser,
  setAmplitudeUserProperties,
  setAmplitudeGroup,
  setAmplitudeGroupProperties,
} from '../shared/amplitude';
import CloudchatSupportWidget from 'dashboard/components/CloudchatSupportWidget.vue';
import CannyChangelogWidget from 'dashboard/components/CannyChangelogWidget.vue';
import { updateDocumentTitle } from 'shared/helpers/pageTitle';
import OnboardingProgressAPI from './api/onboardingProgress';
import {
  buildOnboardingJourneys,
  summarizeProgress,
} from './routes/dashboard/settings/onboarding/onboardingSteps';
import { getUserPermissions } from './helper/permissionsHelper';
import { REDIS_FEATURE_FLAGS } from './featureFlags';

const INTERNAL_EMAIL_DOMAIN = 'cloudhumans.com';

// "Channel::WebWidget" -> "web_widget"; dedupes via Set at call site.
const normalizeChannelType = channelType =>
  channelType
    .replace(/^Channel::/, '')
    .replace(/([a-z])([A-Z])/g, '$1_$2')
    .toLowerCase();

export default {
  name: 'App',

  components: {
    AddAccountModal,
    CloudchatSupportWidget,
    CannyChangelogWidget,
    LoadingState,
    NetworkNotification,
    PaymentPendingBanner,
    WootSnackbarBox,
    UpgradeBanner,
    PendingEmailVerificationBanner,
  },
  data() {
    return {
      showAddAccountModal: false,
      latestChatwootVersion: null,
      reconnectService: null,
    };
  },

  computed: {
    ...mapGetters({
      getAccount: 'accounts/getAccount',
      isRTL: 'accounts/isRTL',
      currentUser: 'getCurrentUser',
      authUIFlags: 'getAuthUIFlags',
      accountUIFlags: 'accounts/getUIFlags',
      currentAccountId: 'getCurrentAccountId',
      currentRole: 'getCurrentRole',
      inboxes: 'inboxes/getInboxes',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      isNewFeatureFlagEnabled: 'isNewFeatureFlagEnabled',
    }),
    hasAccounts() {
      const { accounts = [] } = this.currentUser || {};
      return accounts.length > 0;
    },
    hideOnOnboardingView() {
      return !isOnOnboardingView(this.$route);
    },
  },

  watch: {
    currentUser() {
      if (!this.hasAccounts) {
        this.showAddAccountModal = true;
      }
    },
    currentAccountId() {
      if (this.currentAccountId) {
        this.initializeAccount();
      }
    },
    // The immediate fire inside startCognitoTokenRefresh() runs before the
    // store has resolved currentUser via validityCheck (currentUser starts
    // as `{ id: null }`), so maybeRefreshCognitoToken bails on the id guard
    // and the next attempt is 10 minutes away. Watching the id lets us
    // trigger the first refresh as soon as the user is known, closing the
    // page-load → near-expiry token → 401 gap.
    'currentUser.id'(newId, oldId) {
      if (newId && !oldId) {
        this.maybeRefreshCognitoToken();
        this.maybeFetchAvailableInstances();
      }
    },
  },
  mounted() {
    this.initializeColorTheme();
    this.listenToThemeChanges();
    this.setLocale(window.chatwootConfig.selectedLocale);
    this.startCognitoTokenRefresh();
    this.maybeFetchAvailableInstances();
  },
  beforeDestroy() {
    if (this.reconnectService) {
      this.reconnectService.disconnect();
    }
    this.stopCognitoTokenRefresh();
  },
  methods: {
    initializeColorTheme() {
      setColorTheme(window.matchMedia('(prefers-color-scheme: dark)').matches);
    },
    listenToThemeChanges() {
      const mql = window.matchMedia('(prefers-color-scheme: dark)');
      mql.onchange = e => setColorTheme(e.matches);
    },
    setLocale(locale) {
      this.$root.$i18n.locale = locale;
      // The router's afterEach already titled the initial route while i18n was
      // still on the default locale, so re-title once the account locale lands.
      updateDocumentTitle(this.$route, {
        i18n: this.$root.$i18n,
        store: this.$store,
      });
    },
    startCognitoTokenRefresh() {
      // Poll cadence picked to comfortably beat the backend's 15-minute
      // refresh-on-near-expiry threshold while staying well under the
      // Cognito id_token 24h TTL — gives the SPA at least one refresh
      // attempt before any in-flight Claudia microfrontend request can
      // hit a real Cognito expiry and trigger an involuntary logout.
      const COGNITO_REFRESH_INTERVAL_MS = 10 * 60 * 1000;
      // 1s debounce on visibilitychange: rapid Cmd+Tab cycles fire the
      // event multiple times in close succession; without a debounce
      // each one triggers a /auth/cognito/id_token round-trip.
      const VISIBILITY_DEBOUNCE_MS = 1000;
      // Fire once immediately. A tab can mount with a token already inside
      // the near-expiry window (cross-instance handoff, sessions that
      // survived a deploy, etc) — without this the first refresh attempt
      // is 10 minutes away, which is past the threshold in those cases.
      this.maybeRefreshCognitoToken();
      // Store timer/handler refs as plain instance props (not in data()) so
      // Vue 2 doesn't install getter/setter on them. Reactivity here has
      // no consumer — the values are only ever cleared on teardown.
      this.$_cognitoTokenIntervalId = setInterval(
        () => this.maybeRefreshCognitoToken(),
        COGNITO_REFRESH_INTERVAL_MS
      );
      this.$_cognitoTokenVisibilityHandler = () => {
        if (document.visibilityState !== 'visible') return;
        if (this.$_cognitoVisibilityDebounce) {
          clearTimeout(this.$_cognitoVisibilityDebounce);
        }
        this.$_cognitoVisibilityDebounce = setTimeout(
          () => this.maybeRefreshCognitoToken(),
          VISIBILITY_DEBOUNCE_MS
        );
      };
      document.addEventListener(
        'visibilitychange',
        this.$_cognitoTokenVisibilityHandler
      );
    },
    stopCognitoTokenRefresh() {
      if (this.$_cognitoTokenIntervalId) {
        clearInterval(this.$_cognitoTokenIntervalId);
        this.$_cognitoTokenIntervalId = null;
      }
      if (this.$_cognitoVisibilityDebounce) {
        clearTimeout(this.$_cognitoVisibilityDebounce);
        this.$_cognitoVisibilityDebounce = null;
      }
      if (this.$_cognitoTokenVisibilityHandler) {
        document.removeEventListener(
          'visibilitychange',
          this.$_cognitoTokenVisibilityHandler
        );
        this.$_cognitoTokenVisibilityHandler = null;
      }
    },
    maybeRefreshCognitoToken() {
      if (!this.currentUser?.id) return;
      // Skip users that did not log in via Cognito (email/password fallback,
      // local-only sessions). Without this guard every poll hits the backend
      // just to receive `{ id_token: null }` and get discarded.
      if (!this.currentUser?.cognito_access_token) return;
      this.$store.dispatch('refreshCognitoAccessToken');
    },
    maybeFetchAvailableInstances() {
      if (!this.currentUser?.id) return;
      this.$store.dispatch('fetchAvailableInstances');
    },
    async initializeAccount() {
      // Generation token: a stale inboxes/get for a prior account is dropped
      // below, so it can't re-identify with the wrong account's channels.
      this.$_amplitudeGen = (this.$_amplitudeGen || 0) + 1;
      const gen = this.$_amplitudeGen;
      setAmplitudeUser(this.currentUser.email);
      // Multi-instance note: emails are globally unique across Cloud Chat
      // instances (instance2, instance3, default), so setAmplitudeUser(email)
      // already gives us a stable cross-instance identity. The `instance` user
      // property is for slicing Amplitude charts ("adoption only on
      // instance3"), not disambiguation.
      setDatadogRumUser(this.currentUser);
      // Clear the prior account's inboxes so the initial identify reads no stale channels.
      this.$store.commit('inboxes/SET_INBOXES', []);
      // Clear the prior account's onboarding snapshot so onboarding_active isn't
      // attributed to the PREVIOUS account while the new one's progress loads.
      this.$_onboardingSummary = null;
      // Clear the prior account's tenant group BEFORE the async accounts/get so events
      // during the load window (or a failed fetch that early-returns below) aren't
      // attributed to the PREVIOUS account's tenant. identify sets the real tenant once loaded.
      setAmplitudeGroup('tenant', '(none)');
      setAmplitudeGroupProperties('tenant', '(none)', { account_segment: 'none', tenant_id: null });
      await this.$store.dispatch('accounts/get');
      // accounts/get dedups on populated records — force it so a switch to an
      // account not yet loaded still fetches before identify.
      if (!this.getAccount(this.currentAccountId).id) {
        await this.$store.dispatch('accounts/get', { force: true });
      }
      this.$store.dispatch('setActiveAccount', {
        accountId: this.currentAccountId,
      });
      const account = this.getAccount(this.currentAccountId);
      const { locale, latest_chatwoot_version: latestChatwootVersion } = account;
      const { pubsub_token: pubsubToken } = this.currentUser || {};
      this.setLocale(locale);
      this.latestChatwootVersion = latestChatwootVersion;
      vueActionCable.init(pubsubToken);
      this.$store.dispatch('notifications/unReadCount');
      this.reconnectService = new ReconnectService(this.$store, router);

      this.identifyAmplitudeUser();

      verifyServiceWorkerExistence(registration =>
        registration.pushManager.getSubscription().then(subscription => {
          if (subscription) {
            registerSubscription();
          }
        })
      );

      // Load inboxes, then re-identify with real channels (not awaited — don't
      // block first paint). Gen guard drops a superseded switch's stale fetch.
      this.$store.dispatch('inboxes/get').then(() => {
        if (gen === this.$_amplitudeGen) this.identifyAmplitudeUser();
      });

      // Load onboarding progress, then re-identify with onboarding_active (not
      // awaited — don't block first paint). Same gen guard as inboxes so a
      // superseded account switch's stale fetch is dropped.
      this.fetchOnboardingProgress(gen);
    },
    // Fetches the account's onboarding progress from the same endpoint the
    // Settings > Guia de Configurações screen uses, derives the summary with
    // the shared helpers (so it never drifts from what the user sees), stores
    // it, and re-identifies so onboarding_active reflects the current account.
    async fetchOnboardingProgress(gen) {
      try {
        const { data } = await OnboardingProgressAPI.get();
        // Drop a superseded account switch's stale fetch.
        if (gen !== this.$_amplitudeGen) return;
        const journeys = buildOnboardingJourneys({
          accountId: this.currentAccountId,
          userPermissions: getUserPermissions(
            this.currentUser,
            this.currentAccountId
          ),
          isClaudiaOnly: this.isFeatureEnabledonAccount(
            this.currentAccountId,
            REDIS_FEATURE_FLAGS.CLAUDIA_ONLY
          ),
          inboxes: this.inboxes,
          isFeatureEnabled: flag =>
            this.isFeatureEnabledonAccount(this.currentAccountId, flag),
          isNewFeatureFlagEnabled: this.isNewFeatureFlagEnabled,
          completedStepIds: data.completed || [],
          trackedStepIds: data.tracked || [],
          manualCompletions: data.manual || [],
        });
        this.$_onboardingSummary = summarizeProgress(journeys);
        if (gen === this.$_amplitudeGen) this.identifyAmplitudeUser();
      } catch (error) {
        // Non-blocking: leave onboarding_active unset (null) on failure so we
        // never assert a wrong onboarding state from a failed fetch. Reported
        // instead of swallowed — silently, a broken fetch is indistinguishable
        // from "no onboarding steps for this account", so it would surface as
        // an Amplitude targeting gap rather than an error. Same handling the
        // onboarding screen gives this endpoint (settings/onboarding/Index.vue).
        Sentry.captureException(error);
      }
    },
    // true  = account still has go-live (essential) steps pending → onboarding
    // false = all go-live steps done → onboarding finished
    // null  = progress not loaded yet, or no go-live steps for this role/account
    //         (null unsets the property instead of asserting a state)
    computeOnboardingActive() {
      const summary = this.$_onboardingSummary;
      if (!summary || !summary.goLiveTotalCount) return null;
      return summary.goLiveCompletedCount < summary.goLiveTotalCount;
    },
    identifyAmplitudeUser() {
      const email = this.currentUser?.email || '';
      // Lowercase before comparing to INTERNAL_EMAIL_DOMAIN — a capitalized
      // domain (e.g. "User@CloudHumans.com") must still count as internal.
      const emailDomain = (email.split('@')[1] || '').toLowerCase();
      const account = this.getAccount(this.currentAccountId);
      // Skip on miss: identify sets the whole account snapshot at once, so
      // skipping keeps the prior account intact rather than mixing a new
      // account_id with stale props.
      if (!account.id) return;
      const segment = account.account_segment || 'none';
      // Overwrite the tenant group on EVERY identify (this runs in the
      // gen-guarded path) so switching to a tenant-less account can't leave the
      // user in the PREVIOUS tenant group. '(none)' is the explicit tenant-less bucket.
      const tenantKey = account.tenant_name || '(none)';
      setAmplitudeGroup('tenant', tenantKey);
      // Group PROPERTIES (distinct from user props) — populate the tenant group.
      setAmplitudeGroupProperties('tenant', tenantKey, {
        account_segment: segment,
        tenant_id: account.tenant_id ?? null,
      });
      const enabledChannels = [
        ...new Set(
          (this.inboxes || [])
            .map(inbox => inbox?.channel_type)
            .filter(Boolean)
            .map(normalizeChannelType)
        ),
      ];
      // Raw email + user_name as user properties is a conscious, team-aligned
      // product decision — internal analytics only, Amplitude access restricted.
      // `?? null` unsets absent values (e.g. role on an impersonation login)
      // instead of skipping, so no prior account's value lingers on the profile.
      setAmplitudeUserProperties({
        role: this.currentRole ?? null,
        account_id: this.currentAccountId,
        account_name: account.name ?? null,
        is_internal_user:
          emailDomain === INTERNAL_EMAIL_DOMAIN || segment === 'internal',
        account_segment: segment,
        email,
        user_name: this.currentUser?.name ?? null,
        email_domain: emailDomain,
        // null (unset) when empty so a not-yet-loaded or failed fetch doesn't
        // assert "no channels"; a later load re-identifies with the real list.
        enabled_channels: enabledChannels.length ? enabledChannels : null,
        account_locale: account.locale ?? null,
        account_timezone: account.timezone ?? null,
        instance_id: window.chatwootConfig?.instanceId ?? null,
        tenant_name: account.tenant_name ?? null,
        // true while go-live (essential) onboarding steps are still pending,
        // false once complete, null until the progress fetch resolves. Powers
        // Amplitude onboarding-guide targeting that auto-hides on completion.
        onboarding_active: this.computeOnboardingActive(),
      });
    },
  },
};
</script>

<template>
  <div
    v-if="!authUIFlags.isFetching && !accountUIFlags.isFetchingItem"
    id="app"
    class="flex-grow-0 w-full h-full min-h-0 app-wrapper"
    :class="{ 'app-rtl--wrapper': isRTL }"
    :dir="isRTL ? 'rtl' : 'ltr'"
  >
    <template v-if="currentAccountId">
      <PendingEmailVerificationBanner v-if="hideOnOnboardingView" />
      <PaymentPendingBanner v-if="hideOnOnboardingView" />
      <UpgradeBanner />
    </template>
    <transition name="fade" mode="out-in">
      <router-view />
    </transition>
    <AddAccountModal :show="showAddAccountModal" :has-accounts="hasAccounts" />
    <WootSnackbarBox />
    <NetworkNotification />
    <CloudchatSupportWidget />
    <CannyChangelogWidget />
  </div>
  <LoadingState v-else />
</template>

<style lang="scss">
@import 'vue-easytable/libs/theme-default/index.css';
@import './assets/scss/app';
</style>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
